import React, { useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Heading from 'components/Luxkit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import Image from 'components/Common/Image/Image'
import CarHireCancellationPolicyText from '../CarHireCancellationPolicyText'
import TextLink from 'components/Luxkit/TextLink'
import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import CarHireInclusionsAndDetailsModal from './CarHireInclusionsAndDetailsModal'
import CarHireOfferTileLocation from './CarHireOfferTileLocation'
import CarHireOfferTileVehicleDetailsCondensed from './CarHireOfferTileVehicleDetailsCondensed'
import CarHirePriceDetailsModal from './CarHirePriceDetailsModal'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowPriceDetails from 'components/Luxkit/PricePoints/PriceRowPriceDetails'

const Tile = styled.div`
  padding: ${rem(20)};
  width: 100%;
  display: grid;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  gap: ${rem(8)};
  grid-template-columns: 1fr;
`

const TileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }
`

const CarImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  filter: brightness(1.25);
  height: 100%;
`

interface Props {
  offer: App.CarHireOffer;
  className?: string;
  onReserve?: (offer: App.CarHireOffer) => void;
  priceFormat?: 'per-day' | 'total';
  pickUpDate: string;
  dropOffDate: string;
  pickUpTime: string;
  dropOffTime: string;
}

function CarHireOfferTileCondensed(props: Props) {
  const {
    offer,
    className,
    onReserve = noop,
    priceFormat = 'per-day',
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
  } = props

  const showModal = useContext(ModalContext)
  const onViewInclusions = () => {
    showModal(
      <CarHireInclusionsAndDetailsModal
        offer={offer}
        pickUpDate={pickUpDate}
        dropOffDate={dropOffDate}
        pickUpTime={pickUpTime}
        dropOffTime={dropOffTime}
        showInclusions
        title="All inclusions & details"
      />)
  }

  const onViewPriceDetails = () => {
    showModal(<CarHirePriceDetailsModal offer={offer} onReserve={onReserve} />)
  }

  const desktopImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    url: `${offer.vehicle.image.url}?w=216&dpr=2`,
  }), [offer.vehicle.image])

  const mobileImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    // 'secondary' faces left, 'primary' faces right
    url: `${offer.vehicle.image.url?.replace('primary', 'secondary')}?w=108&dpr=2`,
  }), [offer.vehicle.image])

  return <Tile className={className}>
    {/* desktop */}
    <CSSBreakpoint min="desktop">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <Group direction="horizontal" fullWidth horizontalAlign="end">
            <Image width={80} image={offer.vendor.image} />
          </Group>
        </Group>
        <CarImage>
          <Image width={216} height={121.5} image={desktopImage} />
        </CarImage>
        <LabelGroup>
          <StructureLabel variant="standard">{offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">Guaranteed model</StatusLabel>}
        </LabelGroup>
      </Group>
    </CSSBreakpoint>
    {/* mobile */}
    <CSSBreakpoint max="tablet">
      <Group direction="horizontal" horizontalAlign="space-between">
        <LabelGroup>
          <StructureLabel variant="standard">{offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">Guaranteed model</StatusLabel>}
        </LabelGroup>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <Image width={64} image={offer.vendor.image} />
        </Group>
      </Group>
    </CSSBreakpoint>

    <TileContent>
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={8}>
          <div>
            <Heading as="span" variant="heading3">{offer.vehicle.model}</Heading>{' '}
            {!offer.guarenteedModel && <BodyText wrap="no-wrap" as="span" variant="medium" colour="neutral-three">or similar</BodyText>}
          </div>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
            <VerticalSpacer gap={4}>
              <CarHireOfferTileVehicleDetailsCondensed offer={offer} showMileage={false} />
              <CarHireOfferTileLocation offer={offer} />
            </VerticalSpacer>
            <CSSBreakpoint max="tablet">
              <CarImage>
                <Image width={108} image={mobileImage} />
              </CarImage>
            </CSSBreakpoint>
          </Group>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <CarHireCancellationPolicyText policy={offer.cancellationPolicy} />
          <TextLink size="medium" onClick={onViewInclusions}>View all inclusions & details</TextLink>
        </VerticalSpacer>
      </VerticalSpacer>

      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="end"
        gap={8}
      >
        <div>
          {priceFormat === 'per-day' && <>
            <PriceRowPrice
              size="L"
              price={offer.pricePerDay}
              saleUnit="day"
              taxesAndFees="inclusive"
            />
            {offer.duration > 1 && <PriceRowCaption>
              Total <FormatCurrency value={offer.price} /> for {pluralizeToString('day', offer.duration)}
            </PriceRowCaption>}
          </>}
          {priceFormat === 'total' && <>
            {offer.duration > 1 && <PriceRowPriceCaption>
              For {pluralizeToString('day', offer.duration)}
            </PriceRowPriceCaption>}
            <PriceRowPrice
              size="L"
              price={offer.price}
              saleUnit="total"
              taxesAndFees="inclusive"
            />
            {offer.duration > 1 && <PriceRowCaption>
              <FormatCurrency value={offer.pricePerDay} />/day
            </PriceRowCaption>}
          </>}
          <PriceRowTaxesAndFees taxesAndFees="inclusive" />
          {offer.payOnArrivalAmount > 0 && <PriceRowPriceDetails size="M" onClick={onViewPriceDetails} />}
        </div>
        <TextButton kind="primary" onClick={() => onReserve(offer)}>
          Reserve
        </TextButton>
      </Group>
    </TileContent>
  </Tile>
}

export default React.memo(CarHireOfferTileCondensed)
